import React, { useState, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';

// Context
import MobileContext from 'Shared/contexts/MobileContext';

// Types
import { DataSourceSlugs } from 'Shared/constants/types';

// Components
import CreateItemFromAsanaModal from 'Shared/components/modals/AsanaModal/CreateItemFromAsanaModal';
import CreateItemFromServicenowModal from 'Shared/components/modals/CopyToServicenowModal/CreateItemFromServicenowModal';
import WpButtonDropdown from 'Wpds/components/button/WpButtonDropdown';
import { WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import WpIcon from 'Wpds/components/icon/WpIcon';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useToggle } from 'Shared/utils/hooks';
import { useOrganizationSubscription } from 'Shared/utils/hooks/use-organization-subscription';

// Styles
import './AddItemButtons.scss';

type Props = {
  disabled?: boolean;
  block?: boolean;
  onAddMeetingItem: Function;
  onAddSubItem?: Function;
  onAddSection?: Function;
  onVideoRecorderToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isDiscussionTopicOption?: boolean;
};

function AddItemButtons({
  disabled = false,
  block = false,
  onAddMeetingItem,
  onAddSubItem,
  onAddSection,
  onVideoRecorderToggle,
  isDiscussionTopicOption = false,
}: Props) {
  const { isZoom, isTeamsEmbeddedTab } = useApp();
  const { isFreePlan } = useOrganizationSubscription();
  const { hasAsanaIntegration } = useCurrentUser();
  const isMobile = useContext(MobileContext);
  const showAsanaActions = hasAsanaIntegration && !isFreePlan;
  const hasServicenowIntegration = useSelector(
    state => state.data_sources?.find(({ slug }) => slug === DataSourceSlugs.servicenow)?.connected
  );
  const showServicenowActions = hasServicenowIntegration;
  const showRecordVideoActions = !isMobile && !isZoom && !isTeamsEmbeddedTab;

  const [isAsanaModalOpen, setIsAsanaModalOpen] = useState(false);
  const handleAsanaModalToggle = () => setIsAsanaModalOpen(prevIsAsanaModalOpen => !prevIsAsanaModalOpen);

  const [isServicenowModalOpen, toggleIsServicenowModalOpen] = useToggle(false);

  const handleClickAddMeetingItem = useCallback(() => {
    onAddMeetingItem();
  }, [onAddMeetingItem]);

  return (
    <>
      {isAsanaModalOpen && (
        <CreateItemFromAsanaModal onAddMeetingItem={onAddMeetingItem} isOpen onToggle={handleAsanaModalToggle} />
      )}
      {isServicenowModalOpen && (
        <CreateItemFromServicenowModal
          onAddMeetingItem={onAddMeetingItem}
          isOpen
          onToggle={toggleIsServicenowModalOpen}
        />
      )}
      <WpButtonDropdown
        onClick={handleClickAddMeetingItem}
        variant="secondary"
        block={block}
        disabled={disabled}
        qaTest={`AddItemButton--${isDiscussionTopicOption ? 'topic' : 'action-item'}`}
        addOnDropdownMenu={
          <WpDropdownMenu>
            {onAddSection && (
              <WpDropdownMenuItem onClick={onAddSection}>
                <WpIcon name="arrow-square-right" />
                Add Section
              </WpDropdownMenuItem>
            )}
            <WpDropdownMenuItem onClick={handleClickAddMeetingItem}>
              <WpIcon name="plus-circle" />
              {isDiscussionTopicOption ? 'Add Topic' : 'Add Item'}
            </WpDropdownMenuItem>
            {onAddSubItem && !isDiscussionTopicOption && (
              <WpDropdownMenuItem onClick={onAddSubItem}>
                <WpIcon name="arrow-right" />
                {isDiscussionTopicOption ? 'Add Sub-topic' : 'Add Sub-item'}
              </WpDropdownMenuItem>
            )}
            {showRecordVideoActions && (
              <WpDropdownMenuItem onClick={onVideoRecorderToggle}>
                <WpIcon name="video" />
                {isDiscussionTopicOption ? 'Record Video Topic' : 'Record Video Item'}
              </WpDropdownMenuItem>
            )}
            {showAsanaActions && (
              <WpDropdownMenuItem onClick={handleAsanaModalToggle}>
                <WpIcon name="asana" prefix="fac" />
                Import from Asana
              </WpDropdownMenuItem>
            )}
            {showServicenowActions && (
              <WpDropdownMenuItem onClick={toggleIsServicenowModalOpen}>
                <WpIcon name="arrow-right" />
                Import from ServiceNow
              </WpDropdownMenuItem>
            )}
          </WpDropdownMenu>
        }
      >
        <WpIcon name="plus-circle" />
        {isDiscussionTopicOption ? 'Add Topic' : 'Add Item'}
      </WpButtonDropdown>
    </>
  );
}

export default AddItemButtons;
